<template lang="pug">
.pass-form
  .spinner-container.form-spinner(v-if="!formDataLoaded")
    q-spinner(color="primary", size="3em")
  .pass-form__logs(v-if="showLogs", :class="logsAdditionalClass")
    .pass-form__logs-last-login
      .pass-form__logs-last-login__label {{ lastLogin.label }}
      .pass-form__logs-last-login__content
        span.pass-form__logs-last-login__content-date {{ getLoginDate(lastLogin.date) }}
        span.pass-form__logs-last-login__content-ip {{ getLoginIpAndOS(lastLogin.ip, lastLogin.os_name) }}
    .pass-form__logs-logins(v-if="logins.length > 0")
      .pass-form__logs-logins-header
        .pass-form__logs-logins-header-date {{ loginsHeaders.date }}
        .pass-form__logs-logins-header-ip {{ loginsHeaders.ip }}
      .pass-form__logs-logins-content
        .pass-form__logs-logins-content-item(v-for="login in logins", :key="`${login.ip} ${login.date}`")
          .pass-form__logs-logins-content-item-date {{ getLoginDate(login.date) }}
          .pass-form__logs-logins-content-item-dot-plug
            template(v-for="i in 100") .
          .pass-form__logs-logins-content-item-ip {{ getLoginIpAndOS(login.ip, login.os_name) }}

    .pass-form__logs-button(v-if="logins.length > 0", @click="toggleLogs()")
      inline-svg(:src="require(`../../../../assets/icons/users/chevron-bg.svg`)")
      inline-svg.chevron(:src="require(`../../../../assets/icons/users/chevron.svg`)")

  component(
    v-if="formDataLoaded",
    :is="defaultForm",
    :grid="grid",
    :formData="formData",
    :rowId="rowId",
    :isProfile="true",
    @close-form="closeForm"
  )
    template(v-slot:after, v-if="grid === 'users'")
      .row.justify-center
        .col-12.padding-5px
          .users-history__button
            q-btn(flat, no-caps, @click="() => openHistory(rowId)") {{ actionMenuLocales.history_button }}

  history-modal(v-if="showHistory", :show="showHistory", :path="historyPath", @close="closeHistory()")
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import defaultForm from "./default.vue";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import { parseISO, format } from "date-fns";
import { useEmitter } from "@/services/useEmitter";

import historyModal from "../../historyModal";
import { actionMenuLocales } from "@/services/useLocales";

const props = defineProps({
  /**
   * Текущий grid. Передается из index.vue текущей страницы
   */
  grid: { type: String, default: () => "" },
  /**
   * Дополнительные параметры для отправки запроса на бек
   */
  additionalParams: { type: Object, default: () => {} },
  /**
   * Путь для запроса на бек
   */
  path: { type: String, default: () => "" },
  /**
   * id выбранного ряда. Пример: 1
   */
  rowId: { type: Number, default: () => null },
});

const emit = defineEmits(["close-form"]);
const emitter = useEmitter();

const formDataLoaded = ref(false);
const formData = ref({});
const logsIsOpen = ref(false);
const lastLogin = ref({});
const logins = ref([]);
const loginsHeaders = ref({});
const showHistory = ref(false);

const historyPath = computed(() => `api/v3/users/${props.rowId}/history`);
const logsAdditionalClass = computed(() => (logsIsOpen.value ? "open" : ""));
const showLogs = computed(() => formDataLoaded && lastLogin.value?.date);

const openHistory = () => {
  showHistory.value = true;
};

const closeHistory = () => {
  showHistory.value = false;
};

const getFormData = async () => {
  try {
    const params = props.additionalParams;
    const { data } = await backend.index(props.path, { params });
    formData.value = data.form;
    lastLogin.value = data.last_logins.last_login;
    logins.value = data.last_logins.logins.data;
    loginsHeaders.value = data.last_logins.logins.header;
  } catch (err) {
    await handleError(err);
  } finally {
    formDataLoaded.value = true;
  }
};

const toggleLogs = () => {
  logsIsOpen.value = !logsIsOpen.value;
};

const getLoginDate = date => {
  return format(parseISO(date), "dd.MM.yyyy, HH:mm:ss");
};

const getLoginIpAndOS = (ip, os) => {
  if (os) {
    return `IP: ${ip} — ${os}`;
  }
  return `IP: ${ip}`;
};

const closeForm = data => {
  emit("close-form", data);
};

onMounted(async () => {
  await getFormData();
});
</script>

<style lang="scss" scoped>
.pass-form {
  .pass-form__logs {
    margin: 20px auto;
    width: calc(100% - 40px);
    border-radius: 16px;
    background-color: #eff2ff;
    padding: 12px 12px;
    position: relative;

    .pass-form__logs-button {
      position: absolute;
      left: 0;
      right: 0;
      margin: auto;
      bottom: -20px;
      width: 52px;
    }

    svg {
      margin: auto;
      cursor: pointer;

      &.chevron {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 12px;
        transition: transform 0.4s ease-in-out;
      }
    }

    &.open {
      .pass-form__logs-logins {
        max-height: 220px;
        overflow-y: scroll;
        animation: 0.8s delay-overflow;
        border-top: 1px solid #bdcaff;
        margin-top: 10px;

        @keyframes delay-overflow {
          0% {
            overflow-y: hidden;
          }
          99% {
            overflow-y: hidden;
          }
          100% {
            overflow-y: scroll;
          }
        }
      }

      svg.chevron {
        transform: rotate(180deg);
      }
    }

    &-last-login {
      display: flex;
      justify-content: space-between;

      &__content-date {
        margin-right: 10px;
      }
    }

    &-logins {
      max-height: 0;
      overflow-y: hidden;
      transition: max-height 0.8s;

      &-header {
        display: flex;
        justify-content: space-between;
        font-weight: 600;
        margin-top: 10px;
      }

      &-content-item {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: nowrap;
        margin-top: 10px;

        &-date {
          text-wrap: nowrap;
        }

        &-ip {
          text-wrap: nowrap;
        }

        &-dot-plug {
          overflow: hidden;
          line-height: 14px;
          color: #9dafff;
        }
      }
    }
  }

  .spinner-container {
    position: relative;
    // 128 пикселей это высота хедера и табов
    height: calc(100vh - 128px);
  }

  .no-padding {
    padding: 0 !important;
  }

  .padding-5px {
    padding: 5px;
  }

  .users-history__button {
    width: 100%;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: none;
    color: var(--workflow-form-cancel-button-color);
    border: 1px solid var(--workflow-form-cancel-button-color);

    button {
      width: 100%;
    }
  }
}
</style>
